/**
 * Login.js
 * Handles user authentication form and state
 * Works with HTTP-only cookie based auth system
 *
 * Renders on the sky (SkyPage) in a pale teal card. The form's fields,
 * handlers, API calls, links and redirects are unchanged from the white-card
 * version — only the markup's class names and the wrapper moved. Styles come
 * from SkyForm.css; see docs/features/LANDING.md.
 *
 * Login.css is still imported, deliberately. Its `.error-message` and
 * `.password-input__*` rules are relied on by MyAccount, Register and fourteen
 * Admin/CI components through the global CSS bundle, and dropping the import
 * would take them away. The rules this page no longer uses are listed in
 * docs/TODO.md for a later cleanup.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import { Eye, EyeOff } from 'lucide-react';
import SkyPage from '../LandingSky/SkyPage';
import '../LandingSky/SkyForm.css';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { setUserFromMagicLink, error: authError } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Local UI states
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [verificationError, setVerificationError] = useState(null);
  const [resendMessage, setResendMessage] = useState('');
  const [resendDisabled, setResendDisabled] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setLocalError('');
    setVerificationError(null);

    try {
      const response = await authService.login({ email: formData.email, password: formData.password });

      if (response.user) {
        setUserFromMagicLink(response.user);
        navigate('/my-knowledge-entries');
      }
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.requiresVerification) {
        setVerificationError({
          message: error.response.data.error,
          email: error.response.data.email || formData.email
        });
      } else {
        setLocalError(
          error.response?.data?.message ||
          error.response?.data?.error ||
          'Login failed. Please try again.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!verificationError?.email) return;
    setResendDisabled(true);
    setResendMessage('');

    try {
      await authService.resendVerification(verificationError.email);
      setResendMessage('Verification email sent!');
    } catch {
      setResendMessage('Failed to resend. Please try again.');
    }

    setTimeout(() => setResendDisabled(false), 30000);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };

  const displayError = localError || authError;

  return (
    <SkyPage>
      <main className="sky-form-region">
        <div className="sky-form-spacer-top" />

        <div className="sky-form-card">
          <form onSubmit={handleSubmit} className="sky-form-body">
            <h1 className="sky-form-heading">Login to Constellation KG</h1>

            {verificationError && (
              <div className="sky-form-notice" role="alert">
                <p>{verificationError.message}</p>
                <p>Check your inbox for a verification link.</p>
                <p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendDisabled}
                    className="sky-form-linkbutton"
                  >
                    {resendDisabled ? 'Sent' : 'Resend verification email'}
                  </button>
                </p>
                {resendMessage && <p>{resendMessage}</p>}
              </div>
            )}

            {!verificationError && displayError && (
              <div className="sky-form-error" role="alert">
                {displayError}
              </div>
            )}

            <div className="sky-form-field">
              <label className="sky-form-label" htmlFor="email">Email:</label>
              <input
                className="sky-form-input"
                type="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={isLoading}
                aria-invalid={displayError ? 'true' : 'false'}
              />
            </div>

            <div className="sky-form-field">
              <label className="sky-form-label" htmlFor="password">Password:</label>
              <div className="sky-form-password">
                <input
                  className="sky-form-input"
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  aria-invalid={displayError ? 'true' : 'false'}
                />
                <button
                  type="button"
                  className="sky-form-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff size={20} aria-hidden="true" />
                  ) : (
                    <Eye size={20} aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="sky-form-primary"
              disabled={isLoading}
              aria-busy={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>

            <hr className="sky-form-divider" />

            {/* Forgot Password Link */}
            <p className="sky-form-note">
              <Link className="sky-form-link" to="/forgot-password">Forgot your password?</Link>
            </p>

            {/* Waitlist Link */}
            <p className="sky-form-note">
              Don't have an account?{' '}
              <Link className="sky-form-link" to="/join-waitlist">Join the Waitlist</Link>
            </p>
          </form>
        </div>

        <div className="sky-form-spacer-bottom" />
      </main>
    </SkyPage>
  );
};

export default Login;
